import toInteger from 'lodash/toInteger';
import get from 'lodash/get';
import has from 'lodash/has';
import isNaN from 'lodash/isNaN';
import reduce from 'lodash/reduce';
import { dirify, dedirify } from './dirify';
import defaultState from '../state';
import { VERTICAL_NAMES } from '../common/constants/common';
import { FILTERS as API_LISTING_FILTERS } from '../sdk/listing/Api';
import { deviceMap } from '../request/device/utils';
import { searchParametersSelector } from '~/Serp/selectors';

export const extratValueWithCorrespondingType = value => {
  if (value === 'true' || value === 'false' || typeof value === 'boolean') {
    return value === 'true' || value === true;
  }
  if (!isNaN(+value)) {
    return toInteger(value);
  }
  if (value === 'null') {
    return null;
  }
  return value;
};

export const tranformQuerySeoToSearchParametersObject = (
  { seo, seoWhere, sem, page = 1, type = 1, routeName = '', pageName = '' },
  currentSearchParameters = searchParametersSelector(defaultState)
) => {
  const dedirifiedDecodedSeo = seo ? dedirify(decodeURIComponent(seo)) : '';
  const dedirifiedDecodedSeoWhere = seoWhere ? dedirify(decodeURIComponent(seoWhere)) : '';

  let searchParameters = {
    ...currentSearchParameters,
    isSeoUrl: true,
    what_d: dedirifiedDecodedSeo,
    whatAccented: dedirifiedDecodedSeo,
    where: dedirifiedDecodedSeoWhere,
    page: parseInt(page, 10) || 1,
    type_id: parseInt(type, 10),
    pageName,
    routeName,
  };

  if (!sem) {
    return searchParameters;
  }

  const isSemUrl = extratValueWithCorrespondingType(sem);

  // Is DSA Serp
  if (isSemUrl) {
    searchParameters = {
      ...searchParameters,
      isSemUrl,
      isSeoUrl: false,
      isUserSearch: 0,
    };
  }

  return searchParameters;
};

const getSeo = seo => (seo ? { seo: dirify(seo.toLowerCase()) } : null);
const getSeoWhere = seoWhere => (seoWhere ? { seoWhere: dirify(seoWhere.toLowerCase()) } : '');

export const tranformSearchParamsToQuerySeoParams = searchParameters => {
  const where = get(searchParameters, 'where');

  // This condition is for vertical using where like Jobs due to API call requirements
  const seo = where
    ? getSeo(get(searchParameters, 'what_d'))
    : getSeo(get(searchParameters, 'whatAccented'));

  const query = {
    routeName: get(searchParameters, 'routeName'),
    pageName: get(searchParameters, 'pageName'),
    ...seo,
    ...getSeoWhere(where),
  };

  const page = get(searchParameters, 'page');
  if (page !== 1) {
    query.page = page;
  }

  return query;
};

export const tranformQueryToSearchParametersObject = (query, currentSearchParameters) => {
  const defaultSearchParameters = searchParametersSelector(defaultState);
  const newSearchParameters = {
    filters: {},
  };
  // eslint-disable-next-line guard-for-in, no-restricted-syntax
  for (const key in query) {
    const value = query[key];
    const typedValue = extratValueWithCorrespondingType(query[key]);

    // We need to check if the query param is a direct property of 'searchParameters'
    // or must be a 'searchParameters.filters'.
    //
    // NOTE:
    //  - There is an exception for "type" query param that must be stored as 'searchParameters.type_id'
    //  - There is an exception for "where_d" query param that must be stored as 'searchParameters.where'

    switch (key) {
      case 'type':
        newSearchParameters.type_id = typedValue;
        break;

      case 'sem':
        newSearchParameters.isSemUrl = typedValue;
        break;

      case 'what_d':
        newSearchParameters.what_d = value;
        newSearchParameters.whatAccented = value;
        break;

      case 'where_d':
      case 'where':
        newSearchParameters.where = value;
        break;

      case 'geo_id':
        newSearchParameters.geoId = value;
        break;

      case 'sug':
        newSearchParameters.suggester = value;
        break;
      case 'device':
        newSearchParameters.device = deviceMap(value);
        break;
      default:
        if (has(defaultSearchParameters, key)) {
          newSearchParameters[key] = typedValue;
        } else if (API_LISTING_FILTERS[key]) {
          newSearchParameters.filters[key] = typedValue;
        }
        break;
    }
  }

  if (query.lastAd) {
    newSearchParameters.boost_ads_since = query.lastAd;
  }

  return { ...(currentSearchParameters || defaultSearchParameters), ...newSearchParameters };
};

export const transformSearchParametersToQueryObject = (searchParameters, verticalName) => {
  const type = get(searchParameters, 'type_id');
  const lastAd = get(searchParameters, 'boost_ads_since');
  const filters = get(searchParameters, 'filters');
  const sem = get(searchParameters, 'isSemUrl');
  let searchParametersToOmit = [
    'filters',
    'type_id',
    'sections',
    'country_code',
    'device',
    'isSeoUrl',
    'whatOriginalTyped',
    'whatAccented',
    'isSemUrl',
    'vertical_id',
    'boost_ads_since',
    'isUserFilterInteraction',
    'isUserSearchInteraction',
    'similars',
  ];

  // Omit "where" param from a url if verticalName is not "jobs"
  if (verticalName !== VERTICAL_NAMES.jobs) {
    searchParametersToOmit = [...searchParametersToOmit, 'where'];
  }

  if (get(searchParameters, 'sort') === '') {
    searchParametersToOmit.push('sort');
  }

  let cleanSearchParameters = searchParameters;
  searchParametersToOmit.forEach(key => {
    cleanSearchParameters = { ...cleanSearchParameters, [key]: undefined };
  });
  const query = {
    ...cleanSearchParameters,
    ...filters, // Filters must be added to the query URL
    geoId: get(searchParameters, 'geoId') || false,
    type,
  };

  if (lastAd) {
    query.lastAd = lastAd;
    query.alertLanding = 1;
  }

  if (sem) {
    query.sem = true;
  }

  return query;
};

export const clearSearchParameters = searchParameters => ({
  ...searchParametersSelector(defaultState),
  what_d: get(searchParameters, 'what_d'),
  where: get(searchParameters, 'where'),
  whatAccented: get(searchParameters, 'whatAccented'),
  type_id: get(searchParameters, 'type_id'),
  isSeoUrl: get(searchParameters, 'isSeoUrl'),
  isSemUrl: get(searchParameters, 'isSemUrl'),
  pageName: get(searchParameters, 'pageName'),
  routeName: get(searchParameters, 'routeName'),
  geoId: false,
});

export const transformArrayFiltersToObjectFilters = arrayFilters => {
  if (!arrayFilters) {
    return {};
  }

  const filtersToExclude = ['geo_id'];

  return reduce(
    arrayFilters,
    (result, filter) => {
      if (!filtersToExclude.includes(filter.name) && filter.name && filter.value) {
        result[filter.name] = extratValueWithCorrespondingType(filter.value); // eslint-disable-line no-param-reassign
      }
      return result;
    },
    {}
  );
};

export function shouldRemoveGeoId(filters, isUserSearchInteraction, ignoreGeoId) {
  return (
    has(filters, 'city') ||
    has(filters, 'city_area') ||
    has(filters, 'region') ||
    isUserSearchInteraction ||
    ignoreGeoId
  );
}

export function getWhatByVertical(verticalName, { what, whatSearch }) {
  if (verticalName === VERTICAL_NAMES.jobs) {
    return what;
  }

  return whatSearch || what;
}

export function getWhatAccentedByVertical(verticalName, { whatAccented, what, where }) {
  if (verticalName === VERTICAL_NAMES.jobs) {
    return whatAccented || `${what} ${where}`.trim();
  }

  return whatAccented || what;
}

export function computeParamsForPage(
  page,
  searchParameters,
  verticalName,
  queryOrigin,
  alertLanding,
  tg
) {
  let newSearchParameters = searchParameters;

  newSearchParameters = { ...searchParameters, page };

  let query = transformSearchParametersToQueryObject(newSearchParameters, verticalName);

  if (get(newSearchParameters, 'isSeoUrl')) {
    query = tranformSearchParamsToQuerySeoParams(newSearchParameters);
  }

  if (get(newSearchParameters, 'isSemUrl')) {
    const routeName = get(newSearchParameters, 'routeName');
    if (
      routeName === 'adwords-homes' ||
      routeName === 'adwords-cars' ||
      routeName === 'adwords-jobs'
    ) {
      query.adwordsParams = '';
    } else {
      query.seo = query.what_d; // Add `seo` param for DSA routes
      query.tg = tg || '0-0-{}';
    }
  } else if (queryOrigin) {
    query.origin = queryOrigin;
    if (alertLanding) {
      query.alertLanding = alertLanding;
    }
  }

  // DSA was failing because of this.
  if (!query.seoWhere) {
    query.seoWhere = query.where;
  }

  return query;
}



// WEBPACK FOOTER //
// ./app/utils/searchParameters.js